import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material'
import { assessmentTemplatesApi, assessmentsApi } from '../../api/assessments'
import { FormSection } from '../../components/assessments/FormSection'
import type { AssessmentTemplate } from '../../types/assessments'

const CATEGORIES = [
  'Cyber-Security Maturity',
  'Cloud Adoption Maturity',
  'Cloud Security Maturity',
  'AI Adoption Readiness',
  'Hypervisor → Hyperscaler Licensing',
  'Application Modernisation',
  'Managed Services Readiness',
  'Data Security Maturity'
]

const ICONS = ['📝', '💻', '📊', '🎯', '📚', '🔬', '💡', '⚡', '🌟', '🎨']
const COLORS = [
  '#3498db',
  '#e74c3c',
  '#2ecc71',
  '#f39c12',
  '#9b59b6',
  '#1abc9c',
  '#34495e',
  '#e67e22',
  '#16a085',
  '#c0392b'
]

export const CreateAssessmentPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [template, setTemplate] = useState<AssessmentTemplate | null>(null)

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    category: '',
    icon: '📝',
    color: '#3498db'
  })

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!templateId) return

      try {
        setLoading(true)
        const response = await assessmentTemplatesApi.getAll()
        const foundTemplate = response.data.find((t) => t.id === parseInt(templateId))

        if (foundTemplate) {
          setTemplate(foundTemplate)
          setFormData({
            title: foundTemplate.title,
            subtitle: foundTemplate.subtitle,
            description: foundTemplate.description,
            category: foundTemplate.category,
            icon: foundTemplate.icon,
            color: foundTemplate.color
          })
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load template')
      } finally {
        setLoading(false)
      }
    }

    fetchTemplate()
  }, [templateId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!templateId) return

    try {
      setSaving(true)
      setError(null)
      const assessment = await assessmentsApi.create(templateId, formData)
      navigate(`/assessments/questions/${assessment.uuid || String(assessment.id)}`)
    } catch (err: any) {
      setError(err.message || 'Failed to create assessment')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Container maxWidth='md' sx={{ py: 4 }}>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth='md' sx={{ py: 4 }}>
      <Typography variant='h4' sx={{ mb: 4, fontWeight: 600 }}>
        Create Assessment
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <FormSection title='Basic Information'>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label='Title'
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label='Subtitle'
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Description'
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}
            >
              <TextField
                select
                fullWidth
                label='Category'
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <TextField
                  select
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  label='Icon'
                >
                  {ICONS.map((icon) => (
                    <MenuItem key={icon} value={icon}>
                      {icon}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <TextField
                select
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                label='Color'
              >
                {COLORS.map((color) => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: color
                        }}
                      />
                      {color}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Box>
        </FormSection>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          <Button variant='outlined' onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' disabled={saving} sx={{ minWidth: 150 }}>
            {saving ? <CircularProgress size={24} /> : 'Save & Continue'}
          </Button>
        </Box>
      </form>
    </Container>
  )
}
